import { DataList, FormGroup, InputGroup, Switch } from '@patternfly/react-core';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Thead, Tbody, Tr, Th, Td } from '@patternfly/react-table';
import { StoreState } from '../../../store/store';
import {
    doseSelectors,
    optimizationCreateRoisSet,
    optimizationPrefixSet,
    optimizationTargetUnionRingsEnabledSet,
    optimizationTargetUnionRingsWidthSet,
    optimizationTargetUnionRingsDistanceSet,
    optimizationSkinEnabledSet,
    optimizationSkinWidthSet,
    optimizationTargetSubtractionEnabledSet,
    optimizationTargetSubtractionMarginSet,
    optimizationPlanningRiskVolumesEnabledSet,
    optimizationPlanningRiskVolumesMarginSet,
} from '../../../store/dose/doseSlice';
import { makeSelectFormValidationErrorsForOptimization } from '../../../store/dose/doseSelectors';
import { getFormValidationErrorMessage } from '../../../store/global-types/form-errors';
import { getFormErrorsWorstSeverity } from '../../../store/global-types/store-errors';
import { validationStatusToPatternFly } from '../../../components/validation-types';
import Section from '../Section';
import SectionItem from '../SectionItem';
import SectionHelpText, { SectionVariant } from '../SectionHelpText';
import NumberInputWithUnit from '../../../components/number-input-with-unit';
import { ValidRangeDisplay } from '../../../components/number-input';
import DebouncedTextInput from '../../../components/debounced-text-input';
import HelpPopover from '../../../components/help-popover';
import ValidationHelperText from '../../../components/validation-helper-text';

import '../customization-form.css';
import './dose-output-form.css';

interface DoseOptimizationFormProps {
    outputId: string,
}

const DoseOptimizationForm = (props: DoseOptimizationFormProps) => {
    const { outputId } = props;
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const output = useSelector((state: StoreState) => doseSelectors.selectOutputById(state, outputId));

    const selectOptimizationErrors = useMemo(makeSelectFormValidationErrorsForOptimization, []);
    const optimizationErrors = useSelector((state: StoreState) => selectOptimizationErrors(state, outputId));

    const handleCreateRoisToggled = useCallback((_event: React.FormEvent<HTMLInputElement>, isChecked: boolean) => {
        dispatch(optimizationCreateRoisSet({ id: outputId, isRoiCreationEnabled: isChecked }));
    }, [dispatch, outputId]);

    const handlePrefixChanged = useCallback((value: string) => {
        dispatch(optimizationPrefixSet({ id: outputId, prefix: value }));
    }, [dispatch, outputId]);

    const handleTargetUnionRingsEnabledToggled = useCallback((_event: React.FormEvent<HTMLInputElement>, isChecked: boolean) => {
        dispatch(optimizationTargetUnionRingsEnabledSet({ id: outputId, isEnabled: isChecked }));
    }, [dispatch, outputId]);

    const handleTargetUnionRingsWidthChanged = useCallback((value: number) => {
        dispatch(optimizationTargetUnionRingsWidthSet({ id: outputId, width: value }));
    }, [dispatch, outputId]);

    const handleDistanceChanged = useCallback((index: number, value: number) => {
        dispatch(optimizationTargetUnionRingsDistanceSet({ id: outputId, index, value }));
    }, [dispatch, outputId]);

    const handleSkinEnabledToggled = useCallback((_event: React.FormEvent<HTMLInputElement>, isChecked: boolean) => {
        dispatch(optimizationSkinEnabledSet({ id: outputId, isEnabled: isChecked }));
    }, [dispatch, outputId]);

    const handleSkinWidthChanged = useCallback((value: number) => {
        dispatch(optimizationSkinWidthSet({ id: outputId, width: value }));
    }, [dispatch, outputId]);

    const handleTargetSubtractionEnabledToggled = useCallback((_event: React.FormEvent<HTMLInputElement>, isChecked: boolean) => {
        dispatch(optimizationTargetSubtractionEnabledSet({ id: outputId, isEnabled: isChecked }));
    }, [dispatch, outputId]);

    const handleTargetSubtractionMarginChanged = useCallback((value: number) => {
        dispatch(optimizationTargetSubtractionMarginSet({ id: outputId, margin: value }));
    }, [dispatch, outputId]);

    const handlePlanningRiskVolumesEnabledToggled = useCallback((_event: React.FormEvent<HTMLInputElement>, isChecked: boolean) => {
        dispatch(optimizationPlanningRiskVolumesEnabledSet({ id: outputId, isEnabled: isChecked }));
    }, [dispatch, outputId]);

    const handlePlanningRiskVolumesMarginChanged = useCallback((value: number) => {
        dispatch(optimizationPlanningRiskVolumesMarginSet({ id: outputId, margin: value }));
    }, [dispatch, outputId]);

    if (output === undefined) {
        return null;
    }

    const { optimization } = output;

    return (
        <DataList aria-label="Optimization ROI creation rules" isCompact className="customization-form">

            {/* Panel 1: Create ROIs switch + prefix */}
            <Section
                isModified={false}
                hasValidationError={false}
                validationError={undefined}
            >
                <SectionItem labelWidth={180} label={t('customizationPage.dose.optimizationSection.createRois.label')}>
                    <FormGroup>
                        <InputGroup data-testid="optimization-create-rois-switch">
                            <Switch
                                id={`optimization-create-rois-${outputId}`}
                                label={t('customizationPage.dose.optimizationSection.createRois.on')}
                                labelOff={t('customizationPage.dose.optimizationSection.createRois.off')}
                                isChecked={optimization.isRoiCreationEnabled}
                                onChange={handleCreateRoisToggled}
                            />
                        </InputGroup>
                    </FormGroup>
                </SectionItem>

                {optimization.isRoiCreationEnabled && (
                    <SectionItem labelWidth={180} label={t('customizationPage.dose.optimizationSection.prefix.label')}>
                        <FormGroup>
                            <InputGroup>
                                <DebouncedTextInput
                                    fieldId={`optimization-prefix-${outputId}`}
                                    defaultValue={optimization.prefix}
                                    onDebouncedChange={handlePrefixChanged}
                                    testId={`optimization-prefix-input-${outputId}`}
                                    maxLength={10}
                                />
                                <HelpPopover
                                    headerContent={t('customizationPage.dose.optimizationSection.prefix.helpPopover.title')}
                                    bodyContent={t('customizationPage.dose.optimizationSection.prefix.helpPopover.body')}
                                    useAppendedStyle
                                />
                            </InputGroup>
                        </FormGroup>
                    </SectionItem>
                )}
            </Section>

            {/* Panel 2: Target expansion rings */}
            {optimization.isRoiCreationEnabled && (
                <Section
                    title={t('customizationPage.dose.optimizationSection.targetExpansionRings.title')}
                    isModified={false}
                    hasValidationError={false}
                    validationError={undefined}
                >
                    <SectionHelpText
                        helpText={t('customizationPage.dose.optimizationSection.targetExpansionRings.helpText')}
                    />

                    <SectionItem labelWidth={180} label={t('customizationPage.dose.optimizationSection.targetExpansionRings.enabled.label')}>
                        <FormGroup>
                            <InputGroup data-testid="optimization-rings-enabled-switch">
                                <Switch
                                    id={`optimization-rings-enabled-${outputId}`}
                                    label={t('customizationPage.dose.optimizationSection.targetExpansionRings.enabled.on')}
                                    labelOff={t('customizationPage.dose.optimizationSection.targetExpansionRings.enabled.off')}
                                    isChecked={optimization.targetUnionRings.isEnabled}
                                    onChange={handleTargetUnionRingsEnabledToggled}
                                />
                            </InputGroup>
                        </FormGroup>
                    </SectionItem>

                    {optimization.targetUnionRings.isEnabled && (
                        <>
                            <SectionItem labelWidth={180} label={t('customizationPage.dose.optimizationSection.targetExpansionRings.width.label')}>
                                <FormGroup>
                                    <NumberInputWithUnit
                                        fieldId={`optimization-rings-width-${outputId}`}
                                        defaultValue={optimization.targetUnionRings.width}
                                        onChange={handleTargetUnionRingsWidthChanged}
                                        lowerBound={0.2}
                                        upperBound={2}
                                        step={0.1}
                                        showSpinnerButtons
                                        unit="cm"
                                        validRangeDisplay={ValidRangeDisplay.OnInteraction}
                                        testId="dose-optimization-rings-width-input"
                                    />
                                </FormGroup>
                            </SectionItem>

                            <SectionItem labelWidth={180} label={t('customizationPage.dose.optimizationSection.targetExpansionRings.distances.subtitle')}>
                                <FormGroup>
                                    <Table variant="compact" className="ring-distances-table" data-testid={`optimization-rings-distances-table-${outputId}`}>
                                        <Thead>
                                            <Tr>
                                                <Th>#</Th>
                                                <Th>{t('customizationPage.dose.optimizationSection.targetExpansionRings.distances.innerOffset')}</Th>
                                                <Th>{t('customizationPage.dose.optimizationSection.targetExpansionRings.distances.outerBound')}</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {optimization.targetUnionRings.distances.map((distance, index) => (
                                                <Tr key={index}>
                                                    <Td>{index + 1}</Td>
                                                    <Td>
                                                        <NumberInputWithUnit
                                                            fieldId={`optimization-rings-distance-${outputId}-${index}`}
                                                            defaultValue={distance}
                                                            onChange={(value) => handleDistanceChanged(index, value)}
                                                            lowerBound={0.1}
                                                            upperBound={10}
                                                            step={0.1}
                                                            showSpinnerButtons
                                                            unit="cm"
                                                            validRangeDisplay={ValidRangeDisplay.OnInteraction}
                                                        />
                                                    </Td>
                                                    <Td>
                                                        {/* Last ring (as of now always the third one) is bounded by body contour instead of distance + width */}
                                                        {index === 2
                                                            ? <span className="body-contour-label">{t('customizationPage.dose.optimizationSection.targetExpansionRings.distances.bodyContour')}</span>
                                                            : `${parseFloat((distance + optimization.targetUnionRings.width).toFixed(2))} cm`}
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                    {optimizationErrors.length > 0 && (
                                        <ValidationHelperText
                                            validated={validationStatusToPatternFly(getFormErrorsWorstSeverity(optimizationErrors))}
                                            helperText={optimizationErrors.map(e => getFormValidationErrorMessage(e.errorType)).join(' ')}
                                        />
                                    )}
                                </FormGroup>
                            </SectionItem>
                            <SectionHelpText
                                helpText={t('customizationPage.dose.optimizationSection.targetExpansionRings.sibNote')}
                                variant={SectionVariant.Warning}
                            />
                        </>
                    )}
                </Section>
            )}

            {/* Panel 3: Skin */}
            {optimization.isRoiCreationEnabled && (
                <Section
                    title={t('customizationPage.dose.optimizationSection.skin.title')}
                    isModified={false}
                    hasValidationError={false}
                    validationError={undefined}
                >
                    <SectionHelpText
                        helpText={t('customizationPage.dose.optimizationSection.skin.helpText')}
                    />

                    <SectionItem labelWidth={180} label={t('customizationPage.dose.optimizationSection.skin.enabled.label')}>
                        <FormGroup>
                            <InputGroup data-testid="optimization-skin-enabled-switch">
                                <Switch
                                    id={`optimization-skin-enabled-${outputId}`}
                                    label={t('customizationPage.dose.optimizationSection.skin.enabled.on')}
                                    labelOff={t('customizationPage.dose.optimizationSection.skin.enabled.off')}
                                    isChecked={optimization.skin.isEnabled}
                                    onChange={handleSkinEnabledToggled}
                                />
                            </InputGroup>
                        </FormGroup>
                    </SectionItem>

                    {optimization.skin.isEnabled && (
                        <SectionItem labelWidth={180} label={t('customizationPage.dose.optimizationSection.skin.width.label')}>
                            <FormGroup>
                                <NumberInputWithUnit
                                    fieldId={`optimization-skin-width-${outputId}`}
                                    defaultValue={optimization.skin.width}
                                    onChange={handleSkinWidthChanged}
                                    lowerBound={0.3}
                                    upperBound={1.5}
                                    step={0.1}
                                    showSpinnerButtons
                                    unit="cm"
                                    validRangeDisplay={ValidRangeDisplay.OnInteraction}
                                    testId="dose-optimization-skin-width-input"
                                />
                            </FormGroup>
                        </SectionItem>
                    )}
                </Section>
            )}

            {/* Panel 4: Target subtraction (SIB dose levels) */}
            {optimization.isRoiCreationEnabled && (
                <Section
                    title={t('customizationPage.dose.optimizationSection.targetSubtraction.title')}
                    isModified={false}
                    hasValidationError={false}
                    validationError={undefined}
                >
                    <SectionHelpText
                        helpText={t('customizationPage.dose.optimizationSection.targetSubtraction.helpText')}
                    />

                    <SectionItem labelWidth={180} label={t('customizationPage.dose.optimizationSection.targetSubtraction.enabled.label')}>
                        <FormGroup>
                            <InputGroup data-testid="optimization-subtraction-enabled-switch">
                                <Switch
                                    id={`optimization-subtraction-enabled-${outputId}`}
                                    label={t('customizationPage.dose.optimizationSection.targetSubtraction.enabled.on')}
                                    labelOff={t('customizationPage.dose.optimizationSection.targetSubtraction.enabled.off')}
                                    isChecked={optimization.targetSubtraction.isEnabled}
                                    onChange={handleTargetSubtractionEnabledToggled}
                                />
                            </InputGroup>
                        </FormGroup>
                    </SectionItem>

                    {optimization.targetSubtraction.isEnabled && (
                        <SectionItem labelWidth={180} label={t('customizationPage.dose.optimizationSection.targetSubtraction.margin.label')}>
                            <FormGroup>
                                <NumberInputWithUnit
                                    fieldId={`optimization-subtraction-margin-${outputId}`}
                                    defaultValue={optimization.targetSubtraction.margin}
                                    onChange={handleTargetSubtractionMarginChanged}
                                    lowerBound={0}
                                    upperBound={1}
                                    step={0.1}
                                    showSpinnerButtons
                                    unit="cm"
                                    validRangeDisplay={ValidRangeDisplay.OnInteraction}
                                    testId="dose-optimization-subtraction-margin-input"
                                />
                            </FormGroup>
                        </SectionItem>
                    )}
                </Section>
            )}

            {/* Panel 5: Planning Risk Volumes */}
            {optimization.isRoiCreationEnabled && (
                <Section
                    title={t('customizationPage.dose.optimizationSection.planningRiskVolumes.title')}
                    isModified={false}
                    hasValidationError={false}
                    validationError={undefined}
                >
                    <SectionHelpText
                        helpText={t('customizationPage.dose.optimizationSection.planningRiskVolumes.helpText')}
                    />

                    <SectionItem labelWidth={180} label={t('customizationPage.dose.optimizationSection.planningRiskVolumes.enabled.label')}>
                        <FormGroup>
                            <InputGroup data-testid="optimization-prv-enabled-switch">
                                <Switch
                                    id={`optimization-prv-enabled-${outputId}`}
                                    label={t('customizationPage.dose.optimizationSection.planningRiskVolumes.enabled.on')}
                                    labelOff={t('customizationPage.dose.optimizationSection.planningRiskVolumes.enabled.off')}
                                    isChecked={optimization.planningRiskVolumes.isEnabled}
                                    onChange={handlePlanningRiskVolumesEnabledToggled}
                                />
                            </InputGroup>
                        </FormGroup>
                    </SectionItem>

                    {optimization.planningRiskVolumes.isEnabled && (
                        <SectionItem labelWidth={180} label={t('customizationPage.dose.optimizationSection.planningRiskVolumes.margin.label')}>
                            <FormGroup>
                                <NumberInputWithUnit
                                    fieldId={`optimization-prv-margin-${outputId}`}
                                    defaultValue={optimization.planningRiskVolumes.margin}
                                    onChange={handlePlanningRiskVolumesMarginChanged}
                                    lowerBound={0.1}
                                    upperBound={1}
                                    step={0.1}
                                    showSpinnerButtons
                                    unit="cm"
                                    validRangeDisplay={ValidRangeDisplay.OnInteraction}
                                    testId="dose-optimization-prv-margin-input"
                                />
                            </FormGroup>
                        </SectionItem>
                    )}
                </Section>
            )}

        </DataList>
    );
}

export default DoseOptimizationForm;
